import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Wordmark from '../components/Wordmark';
import { trackFunnel } from '../lib/funnel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PADDLE_CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

const FEATURES = [
  'Prioritised compliance roadmap',
  'Three professional policy documents',
  'Public Trust Page to share with prospects',
  'Live compliance score',
  'Monthly security checklist',
  'Cancel anytime',
];

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [paddleReady, setPaddleReady] = useState(false);
  const [paddleError, setPaddleError] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | processing | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [billingConfig, setBillingConfig] = useState(null);
  const initRef = useRef(false);

  // Fetch price_id + environment from backend (avoids hard-coding in client)
  useEffect(() => {
    trackFunnel('payment_page_viewed');
    if (!user) return;
    axios
      .get(`${API}/billing/status`)
      .then((r) => {
        setBillingConfig(r.data);
        // If user is already active, skip checkout
        if (r.data.subscription_status === 'active') {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => {
        // silent — fall back to env price_id below
      });
  }, [user, navigate]);

  // Inject Paddle.js once
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (!PADDLE_CLIENT_TOKEN) {
      setPaddleError(
        'Payment system not configured. Set REACT_APP_PADDLE_CLIENT_TOKEN.'
      );
      return;
    }

    const existing = document.querySelector('script[data-paddle="v2"]');
    const onReady = () => {
      try {
        const env = billingConfig?.environment || 'production';
        if (window.Paddle?.Environment?.set && env === 'sandbox') {
          window.Paddle.Environment.set('sandbox');
        }
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: handlePaddleEvent,
        });
        setPaddleReady(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Paddle] init failed', e);
        setPaddleError('Could not initialise checkout. Please refresh.');
      }
    };

    if (existing) {
      if (window.Paddle?.Initialize) onReady();
      else existing.addEventListener('load', onReady);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.dataset.paddle = 'v2';
    script.onload = onReady;
    script.onerror = () => setPaddleError('Failed to load Paddle.js');
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingConfig]);

  // Paddle event handler — fires inside event_callback config above
  const handlePaddleEvent = useCallback(
    async (data) => {
      const name = data?.name;
      // eslint-disable-next-line no-console
      console.log('[Paddle event]', name, JSON.stringify(data || {}).slice(0, 500));

      if (name === 'checkout.loaded') {
        setCheckoutOpen(true);
      } else if (name === 'checkout.completed') {
        setPaymentStatus('processing');
        trackFunnel('payment_completed');
        const customerId = data?.data?.customer?.id;
        const transactionId = data?.data?.transaction_id || data?.data?.id;
        try {
          await axios.post(`${API}/billing/link-customer`, {
            paddle_customer_id: customerId,
            transaction_id: transactionId,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[Paddle] link-customer failed', e);
        }
        setPaymentStatus('success');
        // Soft polling fallback: check billing status every 1.5s for up to 10s.
        // Navigates as soon as webhook lands AND user becomes "active" — covers
        // slow webhooks. Falls through to a forced redirect at the timeout.
        const start = Date.now();
        const poll = async () => {
          try {
            const r = await axios.get(`${API}/billing/status`);
            if (r.data?.subscription_status === 'active') {
              navigate('/dashboard', { replace: true });
              return;
            }
          } catch {
            /* ignore transient errors and keep polling */
          }
          if (Date.now() - start < 10000) {
            setTimeout(poll, 1500);
          } else {
            navigate('/dashboard', { replace: true });
          }
        };
        setTimeout(poll, 800);
      } else if (name === 'checkout.error' || name === 'checkout.payment.failed') {
        setPaymentStatus('error');
        setErrorMsg(
          data?.detail ||
            data?.error?.detail ||
            data?.data?.error ||
            'Payment could not be completed. Please try again.'
        );
      } else if (name === 'checkout.closed') {
        setCheckoutOpen(false);
      }
    },
    [navigate]
  );

  const openCheckout = () => {
    setPaymentStatus('idle');
    setErrorMsg('');
    const priceId = billingConfig?.price_id || process.env.REACT_APP_PADDLE_PRICE_ID;
    if (!priceId) {
      setPaddleError('Missing PADDLE_PRICE_ID — please contact support.');
      return;
    }
    if (!window.Paddle?.Checkout?.open) {
      setPaddleError('Checkout not ready yet — please wait a moment.');
      return;
    }
    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: user?.email ? { email: user.email } : undefined,
      settings: {
        displayMode: 'inline',
        frameTarget: 'paddle-checkout-frame',
        frameInitialHeight: 450,
        frameStyle: 'width:100%; min-height:450px; background-color: transparent; border: none;',
        theme: 'light',
        successUrl: `${window.location.origin}/dashboard`,
      },
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#1a1a1a]/60">Loading...</p>
      </div>
    );
  }

  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Wordmark />
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#0a5c44]/10 border border-[#0a5c44]/20 px-3 py-1.5 rounded-full mb-5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0a5c44]" />
            <span className="text-xs font-medium text-[#0a5c44]">
              Compliance package generated
            </span>
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-3 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Your compliance package is ready to unlock
          </h1>
          <p className="text-base sm:text-lg text-[#1a1a1a]/70 max-w-xl mx-auto">
            Subscribe to Verilock to access your roadmap, policies, Trust Page, and monthly checklist.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Left: what you get + price */}
          <div
            data-testid="payment-summary-card"
            className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 order-2 lg:order-1"
          >
            <div className="flex items-baseline gap-2 mb-1">
              <span
                className="text-5xl sm:text-6xl font-normal text-[#1a1a1a]"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                $39
              </span>
              <span className="text-sm sm:text-base text-[#1a1a1a]/60">/month</span>
            </div>
            <p className="text-sm text-[#1a1a1a]/60 mb-6">
              Billed monthly via Paddle. Cancel anytime.
            </p>

            <ul className="space-y-3 mb-6">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0a5c44] shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-[#1a1a1a]/85">{f}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-[#e0ddd8] pt-5 text-xs text-[#1a1a1a]/55 leading-relaxed">
              Secure payment processed by Paddle. Your card never touches our servers.
              VAT/sales tax automatically calculated where applicable.
            </div>
          </div>

          {/* Right: Paddle inline checkout */}
          <div className="order-1 lg:order-2">
            {paddleError && (
              <div
                data-testid="payment-paddle-error"
                className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-5 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{paddleError}</span>
              </div>
            )}

            {paymentStatus === 'error' && (
              <div
                data-testid="payment-error-message"
                className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-5 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Payment failed</p>
                  <p>{errorMsg}</p>
                </div>
              </div>
            )}

            {paymentStatus === 'success' && (
              <div
                data-testid="payment-success-message"
                className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 rounded-lg text-sm mb-5 flex items-start gap-2"
              >
                <Check className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Payment received</p>
                  <p>Taking you to your dashboard...</p>
                </div>
              </div>
            )}

            {!checkoutOpen && paymentStatus !== 'success' && (
              <div className="bg-white border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 text-center">
                <h3
                  className="text-xl sm:text-2xl font-normal mb-2 leading-tight"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  Continue to secure checkout
                </h3>
                <p className="text-sm text-[#1a1a1a]/65 mb-6">
                  Pay with card or Apple/Google Pay via Paddle.
                </p>
                <button
                  type="button"
                  onClick={openCheckout}
                  data-testid="payment-open-checkout-button"
                  disabled={!paddleReady}
                  className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] disabled:bg-[#1a1a1a]/40 disabled:cursor-not-allowed transition-colors h-12 rounded-md text-sm font-medium"
                >
                  {paddleReady ? 'Subscribe — $39/month' : 'Loading checkout...'}
                </button>
                <p className="text-xs text-[#1a1a1a]/45 mt-4">
                  By subscribing you agree to Verilock's Terms and Privacy Policy.
                </p>
              </div>
            )}

            <div
              className={`paddle-checkout-frame ${checkoutOpen ? 'block' : 'hidden'} bg-white border border-[#e0ddd8] rounded-2xl overflow-hidden`}
              data-testid="payment-paddle-frame"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
